import useDragReorder from '../hooks/useDragReorder';
import '../css/Pos.css';

/**
 * A list of rows that can be dragged into order, each with Edit and Remove.
 * Used for a product's customization options and for its extras, so both
 * lists look and behave the same.
 *
 * Uses the same pointer-based drag as the product grid, so it works with a
 * mouse and with a finger on the counter tablet (press and hold).
 *
 * @param {object} props
 * @param {Array<{id: string}>} props.items - the rows, in order
 * @param {(item: object) => {title: string, detail: string}} props.describe -
 *   the bold title and the small line under it, for one row
 * @param {(items: Array<object>) => void} props.onChange - the whole list in its new order
 * @param {(item: object) => void} props.onEdit
 * @param {string} props.hint - what dragging changes, shown under the list
 * @param {boolean} [props.disabled]
 * @returns {JSX.Element|null} nothing when there are no rows
 */
function PosSortableRows({ items, describe, onChange, onEdit, hint, disabled = false }) {
  const canArrange = !disabled && items.length > 1;

  /**
   * Moves the dragged row to the one the pointer is over. Called live as the
   * pointer crosses each row, so the row travels with the drag. An id from
   * another list on the same form (options vs extras) is not found here and
   * is ignored.
   *
   * @param {string} fromId
   * @param {string} toId
   * @returns {void}
   */
  const moveItem = (fromId, toId) => {
    const from = items.findIndex((item) => item.id === fromId);
    const to = items.findIndex((item) => item.id === toId);
    if (from < 0 || to < 0 || from === to) return;
    const next = [...items];
    const [dragged] = next.splice(from, 1);
    next.splice(to, 0, dragged);
    onChange(next);
  };

  const { draggingId, getDragProps } = useDragReorder({ enabled: canArrange, onMove: moveItem });

  if (items.length === 0) return null;

  return (
    <>
      <ul className={`pos-opt-list${canArrange ? ' pos-opt-list-arrange' : ''}`}>
        {items.map((item) => {
          const { title, detail } = describe(item);
          return (
            <li
              key={item.id}
              className={`pos-opt-row${draggingId === item.id ? ' dragging' : ''}`}
              {...getDragProps(item.id)}
            >
              <span className="pos-opt-row-text">
                <strong>{title}</strong>
                {detail && <em>{detail}</em>}
              </span>
              {/* data-no-drag: a press on Edit or Remove is a tap on that
                  button, never the start of a drag. */}
              <span className="pos-opt-row-actions" data-no-drag>
                <button type="button" onClick={() => onEdit(item)} disabled={disabled}>
                  Edit
                </button>
                <button
                  type="button"
                  className="danger"
                  onClick={() => onChange(items.filter((other) => other.id !== item.id))}
                  disabled={disabled}
                >
                  Remove
                </button>
              </span>
            </li>
          );
        })}
      </ul>
      {items.length > 1 && <span className="pos-theme-hint">{hint}</span>}
    </>
  );
}

export default PosSortableRows;
